import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Bookmark, Clapperboard, Crown, Film, Home, LogOut, Menu, Radio, Search, Sparkles, Tag, Tv, User, Zap } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "@/components/brand/Logo";
import { InstallButton } from "@/components/layout/InstallButton";
import { useAuth } from "@/context/AuthContext";
import { useGuest } from "@/context/GuestContext";
import { useCreatorOn } from "@/lib/platform";

const ITEMS = [
  { label: "Watch", to: "/", icon: Home, end: true },
  { label: "Originals", to: "/originals", icon: Crown },
  { label: "Movies", to: "/movies", icon: Film },
  { label: "Series", to: "/series", icon: Tv },
  { label: "Shorts", to: "/shorts", icon: Zap },
  { label: "My Library", to: "/library", icon: Bookmark, gated: "library" },
  { label: "Search", to: "/search", icon: Search },
  { label: "Create", to: "/create", icon: Sparkles, gated: "create" },
  { label: "Live Events", to: "/live-events", icon: Radio },
  { label: "Pricing", to: "/pricing", icon: Tag },
  { label: "Account", to: "/account", icon: User, gated: "account" },
];

export const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  const { logout } = useAuth();
  const { isGuest, guestOn, openAuth } = useGuest();
  const creatorOn = useCreatorOn();
  const navigate = useNavigate();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="mobile-menu-btn" aria-label="Open menu" className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[300px] bg-[#0B0B0D] border-r border-[#D4AF37]/25 text-white p-0" data-testid="mobile-menu">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <div className="px-6 pt-6 pb-4" onClick={() => setOpen(false)}><Logo /></div>
        <div className="gold-hairline" />
        <nav className="flex flex-col px-3 py-4 gap-1 overflow-y-auto max-h-[calc(100vh-200px)]">
          {ITEMS.filter((i) => creatorOn || i.gated !== "create").map(({ label, to, icon: Icon, end, gated }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={(e) => { setOpen(false); if (isGuest && gated) { e.preventDefault(); openAuth({ reason: gated }); } }}
              data-testid={`mobile-menu-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-3 text-[0.95rem] font-semibold transition-colors ${isActive ? "bg-[#D4AF37]/15 text-[#FFD86A]" : "text-white/80 hover:bg-white/5"}`}
            >
              <Icon className="h-5 w-5 text-[#D4AF37]" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 inset-x-0 p-4 flex flex-col gap-2 border-t border-[#D4AF37]/15 bg-[#0B0B0D]">
          <InstallButton className="w-full py-2.5 text-sm" testId="mobile-menu-install-btn" />
          {isGuest ? (
            <button data-testid="mobile-menu-sign-in" onClick={() => { setOpen(false); openAuth(); }} className="btn-gold w-full py-2.5 text-sm">Sign in / Sign up</button>
          ) : (
            <button data-testid="mobile-menu-sign-out" onClick={async () => { await logout(); navigate(guestOn ? "/" : "/login"); }} className="flex items-center justify-center gap-2 py-2.5 text-sm text-[#8B8B8B] hover:text-[#E50914] transition-colors">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const MOBILE_ICONS = { Home, Film, Sparkles, Bookmark, User, Clapperboard };
